import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import Spinner from './ui/Spinner';
import { drillingService } from '../services/drillingService';
import {
  dailyReportService,
  EMPTY_DAILY_REPORT_NARRATIVE,
} from '../services/dailyReportService';
import {
  pickLatestProjectFromSources,
  resolveSourceProjectName,
} from '../utils/drillingCampaignHelpers';
import {
  DAILY_REPORT_PRESETS,
  DEFAULT_DAILY_REPORT_PRESET,
  getDefaultSelectedSections,
  getSectionDefinitionsForUi,
  getApplicableSections,
} from '../utils/dailyReport/dailyReportSections';
import {
  buildSampleProgressForReport,
  formatReportDate,
  toDateInputValue,
} from '../utils/dailyReport/sampleProgressForReport';
import { buildDrillingMetricsForReport } from '../utils/dailyReport/drillingMetricsForReport';
import {
  buildDrillingMapReportContext,
} from '../utils/dailyReport/drillingMapForReport';
import {
  buildInProgressHolePages,
  applyGeologistDisplayNames,
  attachHoleInsetMapSnapshots,
  resolveCampaignDrillzId,
  selectedInProgressHolesEqual,
} from '../utils/dailyReport/drillingInProgressForReport';
import { resolveUserDisplayNames } from '../services/graphUserService';
import { captureDrillHoleMapSnapshot } from '../utils/dailyReport/captureDrillHoleMap';
import { generateDailyReport } from '../utils/dailyReport/dailyReportPdfGenerator';
import {
  loadReportMapViewport,
  saveReportMapViewport,
} from '../utils/dailyReport/reportMapViewport';
import ReportMapZoomModal from './dailyReport/ReportMapZoomModal';
import DailyReportQuicklogPreview from './dailyReport/DailyReportQuicklogPreview';
import {
  fetchListVisualMapsWithCache,
  listVisualMapsToPlainObject,
} from '../utils/drillz/listValueVisuals';
import './DailyReport.css';

const SECTION_DEFINITIONS = getSectionDefinitionsForUi();

function narrativesEqual(a, b) {
  return (
    (a?.compiled_by ?? '') === (b?.compiled_by ?? '') &&
    (a?.positive_safety_observation ?? '') === (b?.positive_safety_observation ?? '') &&
    (a?.incidents ?? '') === (b?.incidents ?? '') &&
    (a?.safety_toolbox_topic ?? '') === (b?.safety_toolbox_topic ?? '') &&
    (a?.geology_meeting_topic ?? '') === (b?.geology_meeting_topic ?? '')
  );
}

const DailyReport = ({ user }) => {
  const [sampleProgressBundle, setSampleProgressBundle] = useState(null);
  const [dashboardData, setDashboardData] = useState([]);
  const [drillzCampaignRows, setDrillzCampaignRows] = useState(null);
  const [projectSources, setProjectSources] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('all');
  const [reportDate, setReportDate] = useState(() => toDateInputValue());
  const [narrative, setNarrative] = useState({ ...EMPTY_DAILY_REPORT_NARRATIVE });
  const [savedNarrative, setSavedNarrative] = useState({ ...EMPTY_DAILY_REPORT_NARRATIVE });
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeSaving, setNarrativeSaving] = useState(false);
  const [narrativeError, setNarrativeError] = useState(null);
  const [inProgressHolesList, setInProgressHolesList] = useState([]);
  const [inProgressHolesLoading, setInProgressHolesLoading] = useState(false);
  const [inProgressHolesError, setInProgressHolesError] = useState(null);
  const [selectedInProgressHoles, setSelectedInProgressHoles] = useState([]);
  const [savedSelectedInProgressHoles, setSavedSelectedInProgressHoles] = useState([]);
  const [selectedSections, setSelectedSections] = useState(() =>
    getDefaultSelectedSections(DEFAULT_DAILY_REPORT_PRESET)
  );
  const [reportPreset, setReportPreset] = useState(DEFAULT_DAILY_REPORT_PRESET);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sampleProgressWarning, setSampleProgressWarning] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [reportMapViewport, setReportMapViewport] = useState(null);
  const [reportMapZoomModalOpen, setReportMapZoomModalOpen] = useState(false);
  const [listVisualMapsPlain, setListVisualMapsPlain] = useState(null);
  const [quicklogPreviewByCollar, setQuicklogPreviewByCollar] = useState({});
  const [quicklogPreviewLoading, setQuicklogPreviewLoading] = useState(false);
  const [quicklogPreviewError, setQuicklogPreviewError] = useState(null);
  const [previewHoleKey, setPreviewHoleKey] = useState('');
  const [projectSourcesUnavailable, setProjectSourcesUnavailable] = useState(false);
  const initialCampaignAppliedRef = useRef(false);
  const printContainerRef = useRef(null);
  const mapCaptureRef = useRef(null);

  const defaultCompiledBy = useMemo(() => {
    return user?.name || user?.displayName || user?.username || user?.email || '';
  }, [user]);

  const reportDirty = useMemo(
    () =>
      !narrativesEqual(narrative, savedNarrative) ||
      !selectedInProgressHolesEqual(selectedInProgressHoles, savedSelectedInProgressHoles),
    [narrative, savedNarrative, selectedInProgressHoles, savedSelectedInProgressHoles]
  );

  const drillzCampaignId = useMemo(
    () => resolveCampaignDrillzId(projectSources, selectedProjectId),
    [projectSources, selectedProjectId]
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSampleProgressWarning(null);
      setProjectSourcesUnavailable(false);

      const [drillingResult, sampleBundle, sourcesResult, drillzRows] = await Promise.all([
        drillingService.fetchDashboardDrillingRows().catch(() => ({ data: [] })),
        drillingService.fetchSampleProgressDashboard(),
        drillingService.fetchProjectSources().then(
          (data) => ({ data, unavailable: false }),
          () => ({ data: [], unavailable: true })
        ),
        drillingService.fetchDrillzCampaignProgress().catch(() => null),
      ]);

      setDashboardData(drillingResult?.data || []);
      setSampleProgressBundle(sampleBundle);
      setProjectSources(Array.isArray(sourcesResult.data) ? sourcesResult.data : []);
      setProjectSourcesUnavailable(sourcesResult.unavailable);
      setDrillzCampaignRows(Array.isArray(drillzRows) ? drillzRows : null);

      if (!sampleBundle) {
        setSampleProgressWarning(
          'Sample progress data is not available. Sample-related sections may be empty.'
        );
      }
    } catch (err) {
      console.error('Error loading daily report data:', err);
      setError(err.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNarrative = useCallback(async () => {
    setNarrativeLoading(true);
    setNarrativeError(null);
    try {
      const result = await dailyReportService.getDailyReport(reportDate);
      const savedHoles = result.selectedInProgressHoles || [];
      if (result.data) {
        setNarrative(result.data);
        setSavedNarrative(result.data);
      } else {
        const empty = {
          ...EMPTY_DAILY_REPORT_NARRATIVE,
          compiled_by: defaultCompiledBy,
        };
        setNarrative(empty);
        setSavedNarrative(empty);
      }
      setSelectedInProgressHoles(savedHoles);
      setSavedSelectedInProgressHoles(savedHoles);
    } catch (err) {
      console.error('Error loading daily report narrative:', err);
      setNarrativeError(err.message || 'Failed to load report details');
      const empty = {
        ...EMPTY_DAILY_REPORT_NARRATIVE,
        compiled_by: defaultCompiledBy,
      };
      setNarrative(empty);
      setSavedNarrative(empty);
      setSelectedInProgressHoles([]);
      setSavedSelectedInProgressHoles([]);
    } finally {
      setNarrativeLoading(false);
    }
  }, [reportDate, defaultCompiledBy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadNarrative();
  }, [loadNarrative]);

  useEffect(() => {
    let cancelled = false;

    async function loadInProgressHoles() {
      if (selectedProjectId === 'all' || !drillzCampaignId) {
        setInProgressHolesList([]);
        setInProgressHolesError(null);
        setInProgressHolesLoading(false);
        return;
      }

      setInProgressHolesLoading(true);
      setInProgressHolesError(null);

      try {
        const result = await drillingService.fetchDrillzInProgressHoles(drillzCampaignId);
        if (!cancelled) {
          setInProgressHolesList(result.data || []);
        }
      } catch (err) {
        console.warn('Drillz in-progress holes unavailable:', err);
        if (!cancelled) {
          setInProgressHolesList([]);
          setInProgressHolesError(err.message || 'Drillz in-progress holes unavailable');
        }
      } finally {
        if (!cancelled) {
          setInProgressHolesLoading(false);
        }
      }
    }

    loadInProgressHoles();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, drillzCampaignId]);

  useEffect(() => {
    if (initialCampaignAppliedRef.current) return;
    if (!sampleProgressBundle && !projectSources.length) return;

    initialCampaignAppliedRef.current = true;

    if (projectSources.length > 0) {
      const latest = pickLatestProjectFromSources(projectSources);
      if (latest?.project_id != null) {
        setSelectedProjectId(String(latest.project_id));
      }
      return;
    }

    const projects = sampleProgressBundle?.projects || [];
    const firstProject = projects.find((p) => p.source_project_id != null);
    if (firstProject?.source_project_id != null) {
      setSelectedProjectId(String(firstProject.source_project_id));
    }
  }, [projectSources, sampleProgressBundle]);

  const campaignTabList = useMemo(() => {
    if (projectSources.length > 0) return projectSources;
    const ids = [
      ...new Set(
        (sampleProgressBundle?.projects || [])
          .map((p) => p.source_project_id)
          .filter((id) => id != null)
      ),
    ];
    return ids.map((project_id) => ({ project_id }));
  }, [projectSources, sampleProgressBundle]);

  const campaignTabLabel = useCallback(
    (src) => {
      const name = resolveSourceProjectName(
        src.project_id,
        sampleProgressBundle?.projects
      );
      if (name) return String(name);
      if (src.source_project_name) return String(src.source_project_name);
      return String(src.project_id ?? 'Project');
    },
    [sampleProgressBundle]
  );

  const sampleMetrics = useMemo(
    () => buildSampleProgressForReport(sampleProgressBundle, selectedProjectId),
    [sampleProgressBundle, selectedProjectId]
  );

  const drillingMetrics = useMemo(
    () =>
      buildDrillingMetricsForReport({
        dashboardData,
        projectSources,
        selectedProjectId,
        sampleProgressBundle,
        drillzCampaignRows,
      }),
    [dashboardData, projectSources, selectedProjectId, sampleProgressBundle, drillzCampaignRows]
  );

  const mapPreviewContext = useMemo(
    () =>
      buildDrillingMapReportContext({
        dashboardData,
        selectedProjectId,
        projectSources,
        sampleProgressBundle,
      }),
    [dashboardData, selectedProjectId, projectSources, sampleProgressBundle]
  );

  useEffect(() => {
    setReportMapViewport(loadReportMapViewport(selectedProjectId));
  }, [selectedProjectId]);

  const previewHolePages = useMemo(
    () =>
      buildInProgressHolePages({
        selectedRefs: selectedInProgressHoles,
        inProgressHolesList,
        dashboardData,
        quicklogByCollar: quicklogPreviewByCollar,
      }),
    [
      selectedInProgressHoles,
      inProgressHolesList,
      dashboardData,
      quicklogPreviewByCollar,
    ]
  );

  useEffect(() => {
    const enabled =
      Boolean(selectedSections.drillingInProgress) && selectedInProgressHoles.length > 0;

    if (!enabled) {
      setQuicklogPreviewByCollar({});
      setQuicklogPreviewError(null);
      setPreviewHoleKey('');
      return undefined;
    }

    let cancelled = false;

    const loadPreviewData = async () => {
      setQuicklogPreviewLoading(true);
      setQuicklogPreviewError(null);

      try {
        const maps = await fetchListVisualMapsWithCache((opts) =>
          drillingService.fetchDrillzListValues(opts)
        );
        if (!cancelled) {
          setListVisualMapsPlain(listVisualMapsToPlainObject(maps));
        }

        const collarUuids = selectedInProgressHoles
          .map((hole) => hole.collar_uuid)
          .filter(Boolean);

        if (collarUuids.length) {
          const quicklogResult = await drillingService.fetchDrillzQuicklog(collarUuids);
          if (!cancelled) {
            setQuicklogPreviewByCollar(quicklogResult.by_collar_uuid || {});
          }
        } else if (!cancelled) {
          setQuicklogPreviewByCollar({});
        }
      } catch (previewErr) {
        console.warn('Quicklog preview load failed:', previewErr);
        if (!cancelled) {
          setQuicklogPreviewError(previewErr.message || 'Quicklog preview unavailable');
        }
      } finally {
        if (!cancelled) {
          setQuicklogPreviewLoading(false);
        }
      }
    };

    loadPreviewData();

    return () => {
      cancelled = true;
    };
  }, [selectedSections.drillingInProgress, selectedInProgressHoles]);

  useEffect(() => {
    if (!previewHolePages.length) {
      setPreviewHoleKey('');
      return;
    }
    const keys = previewHolePages.map((hole) =>
      String(hole.collar_uuid || hole.hole_id || '')
    );
    if (!keys.includes(previewHoleKey)) {
      setPreviewHoleKey(keys[0]);
    }
  }, [previewHolePages, previewHoleKey]);

  const updateNarrativeField = (field, value) => {
    setNarrative((prev) => ({ ...prev, [field]: value }));
  };

  const updateSelectedInProgressHoles = (nextSelected) => {
    setSelectedInProgressHoles(nextSelected);
  };

  const toggleInProgressHole = (hole) => {
    const key = String(hole.collar_uuid || hole.hole_id || '');
    const exists = selectedInProgressHoles.some(
      (entry) => String(entry.collar_uuid || entry.hole_id || '') === key
    );
    const next = exists
      ? selectedInProgressHoles.filter(
          (entry) => String(entry.collar_uuid || entry.hole_id || '') !== key
        )
      : [
          ...selectedInProgressHoles,
          {
            collar_uuid: hole.collar_uuid ?? null,
            hole_id: hole.hole_id ?? null,
          },
        ];
    updateSelectedInProgressHoles(next);
  };

  const selectAllInProgressHoles = () => {
    updateSelectedInProgressHoles(
      inProgressHolesList.map((hole) => ({
        collar_uuid: hole.collar_uuid ?? null,
        hole_id: hole.hole_id ?? null,
      }))
    );
  };

  const clearInProgressHoleSelection = () => {
    updateSelectedInProgressHoles([]);
  };

  const isInProgressHoleSelected = (hole) => {
    const key = String(hole.collar_uuid || hole.hole_id || '');
    return selectedInProgressHoles.some(
      (entry) => String(entry.collar_uuid || entry.hole_id || '') === key
    );
  };

  const handleSaveNarrative = async () => {
    setNarrativeSaving(true);
    setNarrativeError(null);
    try {
      const result = await dailyReportService.upsertDailyReport(reportDate, {
        narrative,
        selectedInProgressHoles,
      });
      const saved = result.data || narrative;
      const savedHoles = result.selectedInProgressHoles || selectedInProgressHoles;
      setNarrative(saved);
      setSavedNarrative(saved);
      setSelectedInProgressHoles(savedHoles);
      setSavedSelectedInProgressHoles(savedHoles);
    } catch (err) {
      console.error('Error saving daily report narrative:', err);
      setNarrativeError(err.message || 'Failed to save report details');
    } finally {
      setNarrativeSaving(false);
    }
  };

  const applyPreset = (preset) => {
    setReportPreset(preset);
    if (preset === 'custom') return;
    const config = DAILY_REPORT_PRESETS[preset];
    if (config) {
      setSelectedSections({ ...config });
    }
  };

  const toggleSection = (sectionId) => {
    setSelectedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
    setReportPreset('custom');
  };

  const handleSaveReportMapViewport = (viewport) => {
    const saved = saveReportMapViewport(selectedProjectId, viewport);
    setReportMapViewport(saved);
  };

  const handleGenerateReport = async () => {
    const sectionsWithCover = { coverPage: true, ...selectedSections };
    const needsDrillingMap = Boolean(selectedSections.currentDrillingLocations);
    const needsInProgressPages = Boolean(selectedSections.drillingInProgress);

    if (needsInProgressPages && selectedInProgressHoles.length === 0) {
      alert('Select at least one in-progress hole, or disable the Drilling in progress section.');
      return;
    }

    let mapReportContext = null;
    if (needsDrillingMap) {
      mapReportContext = buildDrillingMapReportContext({
        dashboardData,
        selectedProjectId,
        projectSources,
        sampleProgressBundle,
      });

      if (!mapReportContext.hasMapData) {
        alert('No geolocated drill holes are available for the selected campaign.');
        return;
      }
    }

    const sectionContext = {
      sampleMetrics,
      drillingMetrics,
      narrative,
      campaignLabel: sampleMetrics?.campaignLabel || drillingMetrics?.campaignLabel,
      mapReportContext,
      inProgressHolePages: [],
      hasInProgressSelection: selectedInProgressHoles.length > 0,
    };

    const applicable = getApplicableSections(sectionsWithCover, sectionContext);

    if (applicable.length === 0) {
      alert('Please select at least one report section.');
      return;
    }

    const needsMetrics = applicable.some((s) => s.id === 'programMetrics');

    if (needsMetrics && !drillingMetrics) {
      alert('Drilling metrics are not available for the selected sections.');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress('Preparing report...');

    try {
      if (needsDrillingMap && mapReportContext) {
        setGenerationProgress('Capturing drilling map...');
        const mapSnapshotDataUrl = await captureDrillHoleMapSnapshot(
          mapCaptureRef.current,
          {
            holes: mapReportContext.holes,
            zoneColorMap: mapReportContext.zoneColorMap,
            zoneFilters: mapReportContext.zoneFilters,
            exportBoundsHoles: mapReportContext.exportBoundsHoles,
            exportViewport: reportMapViewport,
          }
        );
        mapReportContext = {
          ...mapReportContext,
          mapSnapshotDataUrl,
        };
        sectionContext.mapReportContext = mapReportContext;
      }

      if (needsInProgressPages) {
        setGenerationProgress('Loading quicklog data...');
        const collarUuids = selectedInProgressHoles
          .map((hole) => hole.collar_uuid)
          .filter(Boolean);
        let quicklogByCollar = {};
        try {
          const quicklogResult = await drillingService.fetchDrillzQuicklog(collarUuids);
          quicklogByCollar = quicklogResult.by_collar_uuid || {};
        } catch (quicklogErr) {
          console.warn('Quicklog unavailable for PDF generation:', quicklogErr);
        }

        let visualMapsPlain = listVisualMapsPlain;
        if (!visualMapsPlain || !Object.keys(visualMapsPlain).length) {
          setGenerationProgress('Loading Drillz list visuals...');
          const maps = await fetchListVisualMapsWithCache((opts) =>
            drillingService.fetchDrillzListValues(opts)
          );
          visualMapsPlain = listVisualMapsToPlainObject(maps);
          setListVisualMapsPlain(visualMapsPlain);
        }

        sectionContext.inProgressHolePages = buildInProgressHolePages({
          selectedRefs: selectedInProgressHoles,
          inProgressHolesList,
          dashboardData,
          quicklogByCollar,
        }).map((page) => ({
          ...page,
          listVisualMaps: visualMapsPlain,
        }));

        if (sectionContext.inProgressHolePages.length === 0) {
          alert('Selected in-progress holes could not be resolved for PDF generation.');
          setIsGenerating(false);
          setGenerationProgress('');
          return;
        }

        setGenerationProgress('Resolving geologist names...');
        try {
          const geologistEmails = sectionContext.inProgressHolePages
            .map((page) => page.geologist)
            .filter(Boolean);
          const nameMap = await resolveUserDisplayNames(geologistEmails);
          sectionContext.inProgressHolePages = applyGeologistDisplayNames(
            sectionContext.inProgressHolePages,
            nameMap
          );
        } catch (nameErr) {
          console.warn('Geologist name lookup unavailable:', nameErr);
        }

        sectionContext.inProgressHolePages = await attachHoleInsetMapSnapshots(
          sectionContext.inProgressHolePages,
          mapCaptureRef.current,
          {
            dashboardData,
            selectedProjectId,
            onProgress: setGenerationProgress,
          }
        );
      }

      await generateDailyReport({
        reportDate,
        campaignLabel: sectionContext.campaignLabel,
        sampleMetrics,
        drillingMetrics,
        narrative,
        selectedSections: sectionsWithCover,
        mapReportContext: sectionContext.mapReportContext,
        inProgressHolePages: sectionContext.inProgressHolePages,
        hasInProgressSelection: sectionContext.hasInProgressSelection,
        onProgress: setGenerationProgress,
        printContainerRef: printContainerRef.current,
      });

      setGenerationProgress('Report generated successfully!');
      setTimeout(() => {
        setIsGenerating(false);
        setGenerationProgress('');
      }, 2000);
    } catch (err) {
      console.error('Error generating daily report PDF:', err);
      alert(`Failed to generate report: ${err.message}`);
      setIsGenerating(false);
      setGenerationProgress('');
    }
  };

  const saveStatusLabel = narrativeSaving
    ? 'Saving...'
    : reportDirty
      ? 'Unsaved changes'
      : 'Saved';

  const drillingInProgressSectionBlocked =
    Boolean(selectedSections.drillingInProgress) && selectedInProgressHoles.length === 0;

  return (
    <Layout user={user} currentRoute="/daily-report">
      <div className="daily-report">
        <div className="daily-report-header">
          <h1>Daily Report</h1>
          <p>Generate templated PDF reports from operational metrics</p>
        </div>

        {loading && (
          <div className="loading-container">
            <Spinner size="large" />
            <p>Loading report data...</p>
          </div>
        )}

        {error && (
          <Card className="error-card">
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <Button onClick={loadData}>Retry</Button>
          </Card>
        )}

        {!loading && !error && (
          <>
            {projectSourcesUnavailable && (
              <div className="daily-report-info-banner">
                Campaign registry is temporarily unavailable. Using sample progress projects for campaign selection.
              </div>
            )}

            {sampleProgressWarning && (
              <div className="daily-report-info-banner daily-report-info-banner--muted">
                {sampleProgressWarning}
              </div>
            )}

            {campaignTabList.length > 0 && (
              <div className="daily-report-campaign-tabs" role="tablist" aria-label="Campaign">
                <button
                  type="button"
                  role="tab"
                  aria-selected={selectedProjectId === 'all'}
                  className={`campaign-tab-btn${selectedProjectId === 'all' ? ' campaign-tab-btn--active' : ''}`}
                  onClick={() => setSelectedProjectId('all')}
                >
                  All
                </button>
                {campaignTabList.map((src) => (
                  <button
                    key={String(src.project_id)}
                    type="button"
                    role="tab"
                    aria-selected={selectedProjectId === String(src.project_id)}
                    className={`campaign-tab-btn${
                      selectedProjectId === String(src.project_id) ? ' campaign-tab-btn--active' : ''
                    }`}
                    onClick={() => setSelectedProjectId(String(src.project_id))}
                  >
                    {campaignTabLabel(src)}
                  </button>
                ))}
              </div>
            )}

            <Card className="details-card">
              <div className="details-card-header">
                <h2>Report Details</h2>
                <span
                  className={`save-status${
                    reportDirty ? ' save-status--dirty' : ''
                  }${narrativeSaving ? ' save-status--saving' : ''}`}
                >
                  {saveStatusLabel}
                </span>
              </div>

              {narrativeLoading ? (
                <div className="details-loading">
                  <Spinner size="small" />
                  <span>Loading report details...</span>
                </div>
              ) : (
                <>
                  <div className="daily-report-field">
                    <label htmlFor="compiled-by">Compiled by</label>
                    <input
                      id="compiled-by"
                      type="text"
                      value={narrative.compiled_by}
                      onChange={(e) => updateNarrativeField('compiled_by', e.target.value)}
                      placeholder="e.g. Riley & Juan"
                    />
                  </div>

                  <div className="daily-report-field">
                    <label htmlFor="report-date">Report date</label>
                    <input
                      id="report-date"
                      type="date"
                      value={reportDate}
                      onChange={(e) => setReportDate(e.target.value)}
                    />
                    <p className="daily-report-hint">
                      Narrative fields are saved per date (program-wide). Auto metrics use the campaign tab above.
                    </p>
                  </div>

                  <div className="daily-report-field">
                    <label htmlFor="positive-safety">Positive safety observation</label>
                    <textarea
                      id="positive-safety"
                      rows={3}
                      value={narrative.positive_safety_observation}
                      onChange={(e) =>
                        updateNarrativeField('positive_safety_observation', e.target.value)
                      }
                    />
                  </div>

                  <div className="daily-report-field">
                    <label htmlFor="incidents">Incidents</label>
                    <textarea
                      id="incidents"
                      rows={4}
                      value={narrative.incidents}
                      onChange={(e) => updateNarrativeField('incidents', e.target.value)}
                    />
                  </div>

                  <div className="daily-report-field">
                    <label htmlFor="safety-toolbox">Safety toolbox topic</label>
                    <textarea
                      id="safety-toolbox"
                      rows={2}
                      value={narrative.safety_toolbox_topic}
                      onChange={(e) =>
                        updateNarrativeField('safety_toolbox_topic', e.target.value)
                      }
                    />
                  </div>

                  <div className="daily-report-field">
                    <label htmlFor="geology-meeting">Geology meeting topic</label>
                    <textarea
                      id="geology-meeting"
                      rows={2}
                      value={narrative.geology_meeting_topic}
                      onChange={(e) =>
                        updateNarrativeField('geology_meeting_topic', e.target.value)
                      }
                    />
                  </div>

                  {narrativeError && <p className="daily-report-error-text">{narrativeError}</p>}

                  <Button
                    onClick={handleSaveNarrative}
                    disabled={narrativeSaving || narrativeLoading || !reportDirty}
                    className="daily-report-save-btn"
                  >
                    {narrativeSaving ? 'Saving...' : 'Save Report Details'}
                  </Button>
                </>
              )}
            </Card>

            <Card className="details-card in-progress-card">
              <div className="details-card-header">
                <h2>Drilling in Progress</h2>
                <span className="in-progress-selection-count">
                  {selectedInProgressHoles.length} selected
                </span>
              </div>

              {selectedProjectId === 'all' ? (
                <p className="daily-report-hint">
                  Select a campaign tab above to load recent in-progress holes from Drillz.
                </p>
              ) : !drillzCampaignId ? (
                <p className="daily-report-hint">
                  This campaign has no Drillz campaign ID configured in project sources.
                </p>
              ) : inProgressHolesLoading ? (
                <div className="details-loading">
                  <Spinner size="small" />
                  <span>Loading in-progress holes...</span>
                </div>
              ) : (
                <>
                  {inProgressHolesError && (
                    <div className="daily-report-info-banner daily-report-info-banner--muted">
                      {inProgressHolesError}
                    </div>
                  )}

                  <div className="in-progress-actions">
                    <button
                      type="button"
                      className="preset-btn"
                      onClick={selectAllInProgressHoles}
                      disabled={!inProgressHolesList.length}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="preset-btn"
                      onClick={clearInProgressHoleSelection}
                      disabled={!selectedInProgressHoles.length}
                    >
                      Clear
                    </button>
                  </div>

                  {!inProgressHolesList.length && !inProgressHolesError ? (
                    <p className="daily-report-hint">
                      No in-progress holes found for this campaign.
                    </p>
                  ) : (
                    <div className="in-progress-hole-list">
                      {inProgressHolesList.map((hole) => {
                        const key = String(hole.collar_uuid || hole.hole_id);
                        return (
                          <label key={key} className="in-progress-hole-item">
                            <input
                              type="checkbox"
                              checked={isInProgressHoleSelected(hole)}
                              onChange={() => toggleInProgressHole(hole)}
                            />
                            <span className="in-progress-hole-label">
                              {hole.display_label || hole.hole_id}
                            </span>
                            {hole.drilling_started && (
                              <span className="in-progress-hole-meta">
                                started {hole.drilling_started}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {drillingInProgressSectionBlocked && (
                    <p className="daily-report-hint in-progress-section-warning">
                      Select at least one hole to include the Drilling in progress PDF section.
                    </p>
                  )}

                  {selectedSections.drillingInProgress &&
                  selectedInProgressHoles.length > 0 ? (
                    <DailyReportQuicklogPreview
                      holes={previewHolePages}
                      selectedHoleKey={previewHoleKey}
                      onSelectHoleKey={setPreviewHoleKey}
                      quicklogByCollar={quicklogPreviewByCollar}
                      listVisualMaps={listVisualMapsPlain}
                      loading={quicklogPreviewLoading}
                      error={quicklogPreviewError}
                    />
                  ) : null}
                </>
              )}
            </Card>

            <div className="daily-report-generator-grid">
              <Card className="controls-card">
                <h2>Report Configuration</h2>

                <div className="daily-report-field">
                  <label>Report preset</label>
                  <div className="daily-report-preset-row">
                    {[
                      { id: 'minimal', label: 'Minimal' },
                      { id: 'standard', label: 'Standard' },
                      { id: 'custom', label: 'Custom' },
                    ].map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        className={`preset-btn${reportPreset === preset.id ? ' preset-btn--active' : ''}`}
                        onClick={() => applyPreset(preset.id)}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="daily-report-field">
                  <label>Sections</label>
                  <div className="daily-report-sections">
                    <label className="section-check section-check--disabled">
                      <input type="checkbox" checked disabled readOnly />
                      Cover page
                    </label>
                    {SECTION_DEFINITIONS.map(({ id, label }) => (
                      <label key={id} className="section-check">
                        <input
                          type="checkbox"
                          checked={Boolean(selectedSections[id])}
                          onChange={() => toggleSection(id)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {selectedSections.currentDrillingLocations && mapPreviewContext.hasMapData && (
                  <div className="daily-report-field">
                    <label>Drilling locations map</label>
                    <div className="report-map-zoom-row">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setReportMapZoomModalOpen(true)}
                        disabled={isGenerating}
                      >
                        Set Report Zoom
                      </Button>
                      <span className="daily-report-hint report-map-zoom-status">
                        {reportMapViewport
                          ? `Custom zoom saved (z${reportMapViewport.zoom.toFixed(2)})`
                          : 'Automatic framing from in-progress collars'}
                      </span>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleGenerateReport}
                  disabled={isGenerating || drillingInProgressSectionBlocked}
                  className="daily-report-generate-btn"
                >
                  {isGenerating ? 'Generating Report...' : 'Generate PDF Report'}
                </Button>

                {generationProgress && (
                  <p className="daily-report-progress">{generationProgress}</p>
                )}
              </Card>

              <Card className="content-card">
                <h2>Metrics Preview</h2>
                {!sampleMetrics && !drillingMetrics ? (
                  <div className="no-data">
                    <p>Metrics are not available.</p>
                  </div>
                ) : (
                  <>
                    <p className="daily-report-preview-meta">
                      {formatReportDate(reportDate)} ·{' '}
                      {sampleMetrics?.campaignLabel || drillingMetrics?.campaignLabel}
                    </p>

                    {drillingMetrics && (
                      <div className="daily-report-preview-grid daily-report-preview-grid--wide">
                        <div className="preview-stat">
                          <span className="preview-stat-value">
                            {drillingMetrics.totalHoles.toLocaleString()}
                          </span>
                          <span className="preview-stat-label">Total Holes</span>
                        </div>
                        <div className="preview-stat">
                          <span className="preview-stat-value">
                            {drillingMetrics.inProgressHoles.toLocaleString()}
                          </span>
                          <span className="preview-stat-label">In Progress</span>
                        </div>
                        <div className="preview-stat">
                          <span className="preview-stat-value">
                            {drillingMetrics.drilledPct.toFixed(1)}%
                          </span>
                          <span className="preview-stat-label">Drilled</span>
                        </div>
                        <div className="preview-stat">
                          <span className="preview-stat-value">
                            {drillingMetrics.loggedPct.toFixed(1)}%
                          </span>
                          <span className="preview-stat-label">Logged</span>
                        </div>
                      </div>
                    )}

                    {sampleMetrics && (
                      <div className="daily-report-preview-grid">
                        <div className="preview-stat">
                          <span className="preview-stat-value">
                            {sampleMetrics.designated.toLocaleString()}
                          </span>
                          <span className="preview-stat-label">Designated</span>
                        </div>
                        <div className="preview-stat">
                          <span className="preview-stat-value">
                            {sampleMetrics.bagged.toLocaleString()}
                          </span>
                          <span className="preview-stat-label">Bagged</span>
                          <span className="preview-stat-pct">
                            {sampleMetrics.pctBagged.toFixed(1)}%
                          </span>
                        </div>
                        <div className="preview-stat">
                          <span className="preview-stat-value">
                            {sampleMetrics.atLab.toLocaleString()}
                          </span>
                          <span className="preview-stat-label">At Lab</span>
                          <span className="preview-stat-pct">
                            {sampleMetrics.pctAtLab.toFixed(1)}%
                          </span>
                        </div>
                        <div className="preview-stat">
                          <span className="preview-stat-value">
                            {sampleMetrics.finalized.toLocaleString()}
                          </span>
                          <span className="preview-stat-label">Finalized</span>
                          <span className="preview-stat-pct">
                            {sampleMetrics.pctFinalized.toFixed(1)}%
                          </span>
                        </div>
                        <div className="preview-stat">
                          <span className="preview-stat-value">
                            {sampleMetrics.qaqcPassed.toLocaleString()}
                          </span>
                          <span className="preview-stat-label">QAQC Passed</span>
                          <span className="preview-stat-pct">
                            {sampleMetrics.pctQaqcPassed.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card>
            </div>
          </>
        )}

        <div
          ref={printContainerRef}
          className="daily-report-print-host"
          aria-hidden="true"
        />
        <div
          ref={mapCaptureRef}
          className="daily-report-map-capture-host"
          aria-hidden="true"
        />

        <ReportMapZoomModal
          open={reportMapZoomModalOpen}
          onClose={() => setReportMapZoomModalOpen(false)}
          mapContext={mapPreviewContext}
          savedViewport={reportMapViewport}
          onSave={handleSaveReportMapViewport}
        />
      </div>
    </Layout>
  );
};

export default DailyReport;
